import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash/collection';
import { debounce } from 'lodash/function';
import { isEmpty } from 'lodash/lang';

import ApplyAndResetButtons from 'components/ApplyAndResetButtons/ApplyAndResetButtons';
import BasicMultiselect from 'components/eui/EuiFiltersContext/controls/BasicMultiselect/BasicMultiselect';
import { useEuiFiltersDispatch, useEuiFiltersState } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { setCacheProcessor, setInitialStateProcessor } from 'components/eui/EuiFiltersContext/processors';
import { FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';

import { StudySiteApi } from '../../../../../api';
import StatusApiService from '../../../../../api/patient/SitePatientApi';
import { defaultMultipleSelectedValueTemplateFunction } from '../../../../../common/data-entry/MultiSelect/MultiSelect';
import Button from '../../../../../common/general/Button';
import ButtonGroup from '../../../../../common/general/ButtonGroup';
import Icon from '../../../../../common/general/Icon';
import { ADD_PATIENT } from '../../../../../constants/userOperations';
import { i18nt } from '../../../../../root';
import { userHasAccessTo } from '../../../../../services/auth';
import { NSPP_PATIENT } from '../../../../../services/i18n/namespaces';
import StudySingleSiteMultiselect from '../../../../eui/EuiFiltersContext/controls/StudySingleSiteMultiselect/StudySingleSiteMultiselect';
import FiltersContainer from '../../../../PageInfoHeader/FiltersContainer/FiltersContainer';
import { INITIAL_FILTERS_STATE } from '../constants';

const debounceCache = debounce(cb => cb(), 500);

export default function FiltersSection({ gridData, onApply, applyDisabled, addPatient }) {
  const { t } = useTranslation(NSPP_PATIENT);
  const state = useEuiFiltersState();
  const dispatch = useEuiFiltersDispatch();

  const { schema } = state;

  const status = state.schema?.status;

  useEffect(
    function() {
      if (status !== FRS_READY) {
        return;
      }
      debounceCache(function() {
        dispatch({
          type: 'PERFORM',
          processor: setCacheProcessor
        });
      });
    },
    [dispatch, gridData, status]
  );

  useEffect(
    function() {
      if (status) return;
      dispatch({
        type: 'PERFORM_INITIALIZATION',
        processor: setInitialStateProcessor,
        payload: {
          ...INITIAL_FILTERS_STATE,
          cacheCollector
        }
      });
    },
    [dispatch, status]
  );

  if (isEmpty(schema)) return null;

  return (
    <PageInfoHeader
      right={
        <ButtonGroup>
          {userHasAccessTo(ADD_PATIENT) && (
            <Button size="h28" onClick={addPatient}>
              <Icon>add</Icon> {t(`patient`)}
            </Button>
          )}
        </ButtonGroup>
      }
    >
      <FiltersContainer loading={status !== FRS_READY}>
        <StudySingleSiteMultiselect uniqKey="ssu" state={state} dispatch={dispatch} getStudySites={getStudySites} />
        <BasicMultiselect
          uniqKey="statuses"
          state={state}
          dispatch={dispatch}
          getOptions={getStatuses}
          label={t('status')}
          customOptionTemplateFunction={customOptionTemplateFunction}
          customSelectedValueTemplateFunction={customSelectedValueTemplateFunction}
        />
        {status === FRS_READY && (
          <ApplyAndResetButtons applyClassName="apply-btn" onApply={onApply} applyDisabled={applyDisabled} />
        )}
      </FiltersContainer>
    </PageInfoHeader>
  );
}

async function getStudySites() {
  const { data: studySites } = await StudySiteApi.getAllStudySites();
  return { studySites, selectedStudyId: null };
}

async function getStatuses() {
  const { data: statuses } = await StatusApiService.getAllStatuses();
  return { source: sortBy(statuses, ['name']) };
}

function cacheCollector(state) {
  return {
    statuses: {
      value: state?.statuses?.value
    },
    ssu: {
      selectedStudyId: state?.ssu?.selectedStudyId,
      selectedSiteMap: state?.ssu?.selectedSiteMap
    }
  };
}

function customOptionTemplateFunction(option) {
  return i18nt(`statuses.${option.name}`, { ns: NSPP_PATIENT });
}

function customSelectedValueTemplateFunction(options) {
  return defaultMultipleSelectedValueTemplateFunction(
    options.map(option => ({ ...option, name: customOptionTemplateFunction(option) }))
  );
}
